// ABOUTME: Page showing a single square pack's title and its squares.
// ABOUTME: Supports adding, renaming, and deleting squares, with a back link to square packs.
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useAuth } from '@clerk/react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import { AddItemDialog } from '../components/AddItemDialog'
import { AiGenerateDialog } from '../components/AiGenerateDialog'
import { Pagination } from '../components/Pagination'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import Switch from '@mui/material/Switch'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Alert from '@mui/material/Alert'
import { FormCard, FormCardHeader, FormCardBody, FormCardFooter, FormCardHeaderStart, FormCardHeaderEnd, InlineContent } from '../theme'
import {
  squarePacksControllerGetOne,
  squarePacksControllerGetSquares,
  squarePacksControllerAddSquare,
  squarePacksControllerUpdateSquare,
  squarePacksControllerDeleteSquare,
  squarePacksControllerUpdate,
  squarePacksControllerGenerateSquareSuggestions,
  type SquarePackDto,
  type GameSquareDto,
} from '../api/generated'
import { ApiError } from '../api/client'

export function SquarePackDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const [pack, setPack] = useState<SquarePackDto | null>(null)
  const [squares, setSquares] = useState<GameSquareDto[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [aiDialogOpen, setAiDialogOpen] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [editingSquareId, setEditingSquareId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [shareError, setShareError] = useState<string | null>(null)
  const cancellingRef = useRef(false)

  useEffect(() => {
    async function fetch() {
      const token = await getToken()
      const [packRes, squaresRes] = await Promise.all([
        squarePacksControllerGetOne(id!, { headers: { Authorization: `Bearer ${token}` } }),
        squarePacksControllerGetSquares(id!, { page, limit: 50 }, { headers: { Authorization: `Bearer ${token}` } }),
      ])
      setPack(packRes.data)
      setSquares(squaresRes.data.items as unknown as GameSquareDto[])
      setTotalPages(squaresRes.data.totalPages)
    }
    fetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshKey, page])

  async function handleAdd() {
    setAddError(null)
    if (!newTitle.trim()) {
      setAddError('Title is required')
      return
    }
    const token = await getToken()
    try {
      await squarePacksControllerAddSquare(id!, { title: newTitle }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setNewTitle('')
      setRefreshKey((k) => k + 1)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setAddError('A square with that name already exists in this pack')
      } else {
        throw err
      }
    }
  }

  async function handleGenerate(topic: string): Promise<string[]> {
    const token = await getToken()
    const res = await squarePacksControllerGenerateSquareSuggestions(
      id!,
      { topic },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    return res.data.suggestions
  }

  async function handleAddSquares(titles: string[]): Promise<void> {
    const token = await getToken()
    await Promise.all(
      titles.map((title) =>
        squarePacksControllerAddSquare(id!, { title }, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ),
    )
    setRefreshKey((k) => k + 1)
  }

  function handleStartEdit(squareId: string, currentTitle: string) {
    setEditingSquareId(squareId)
    setEditingTitle(currentTitle)
    setEditError(null)
  }

  async function handleSaveEdit(squareId: string) {
    setEditError(null)
    const token = await getToken()
    try {
      await squarePacksControllerUpdateSquare(id!, squareId, { title: editingTitle }, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setEditingSquareId(null)
      setRefreshKey((k) => k + 1)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setEditError('A square with that name already exists in this pack')
      } else {
        throw err
      }
    }
  }

  function handleCancelEdit() {
    cancellingRef.current = true
    setEditingSquareId(null)
    setEditingTitle('')
    setEditError(null)
  }

  async function handleDelete(squareId: string) {
    const token = await getToken()
    await squarePacksControllerDeleteSquare(id!, squareId, {
      headers: { Authorization: `Bearer ${token}` },
    })
    setSquares((prev) => prev.filter((sq) => sq.id !== squareId))
  }

  async function handleToggleFree(squareId: string, currentFree: boolean) {
    const newFree = !currentFree
    // Optimistic update: toggle this square; if setting free, clear it from all others
    setSquares(prev => prev.map(sq => {
      if (sq.id === squareId) return { ...sq, free: newFree }
      if (newFree && sq.free) return { ...sq, free: false }
      return sq
    }))
    try {
      const token = await getToken()
      await squarePacksControllerUpdateSquare(id!, squareId, { free: newFree }, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch (err) {
      // Revert and re-fetch authoritative state on error
      setRefreshKey((k) => k + 1)
      console.error('[toggleFree] API error:', err)
    }
  }

  async function handleSharedToggle(checked: boolean) {
    setShareError(null)
    const token = await getToken()
    try {
      const res = await squarePacksControllerUpdate(
        id!,
        { shared: checked },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      setPack(res.data)
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 422) {
        setShareError('This pack has been used in games and cannot be made private.')
        // Snap the toggle back to its previous state
        setPack((prev) => prev ? { ...prev, shared: !checked } : prev)
      } else {
        throw err
      }
    }
  }

  if (!pack) return null

  return (
    <Box pt={4}>
      <FormCard>
        <FormCardHeader>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormCardHeaderStart direction="row" alignItems="center" spacing={1}>
              <IconButton color="inherit" aria-label="back" onClick={() => navigate('/square-packs')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1" noWrap>{pack.title}</Typography>
            </FormCardHeaderStart>
            <FormCardHeaderEnd direction="row" alignItems="center" spacing={2}>
              <Tooltip title="Generate with AI">
                <IconButton color="inherit" onClick={() => setAiDialogOpen(true)} aria-label="Generate with AI">
                  <AutoFixHighIcon />
                </IconButton>
              </Tooltip>
              <Button color="inherit" variant="outlined" onClick={() => setDialogOpen(true)}>Add Square</Button>
            </FormCardHeaderEnd>
          </Stack>
        </FormCardHeader>
        <FormCardBody>
          <Stack spacing={2}>
          <InlineContent>
          <Tooltip title="A game needs at least 25 squares to open. More squares give each player a unique card.">
            <Chip
              label={squares.length >= 25
                ? `${squares.length} squares`
                : `${squares.length} / 25 squares`}
              color={squares.length >= 25 ? 'success' : 'warning'}
              size="small"
            />
          </Tooltip>
          </InlineContent>
          {pack.ownerId !== null && (
            <FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={pack.shared}
                    onChange={(e) => handleSharedToggle(e.target.checked)}
                    inputProps={{ 'aria-label': 'shareable' }}
                  />
                }
                label="Shareable"
              />
              <FormHelperText>When on, anyone with the link can use this pack to create a game.</FormHelperText>
              {shareError && <Alert severity="warning">{shareError}</Alert>}
            </FormControl>
          )}
          {squares.length === 0 ? (
            <Stack direction="row" alignItems="center" flexWrap="wrap">
              <Typography color="text.secondary">No squares yet. Add one to get started, or</Typography>
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={() => setAiDialogOpen(true)}
              >
                Generate with AI
              </Button>
            </Stack>
          ) : (
            <List disablePadding>
              {squares.map((sq) => (
                <ListItem
                  key={sq.id}
                  secondaryAction={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label="FREE"
                        variant={sq.free ? 'filled' : 'outlined'}
                        color={sq.free ? 'primary' : 'default'}
                        size="small"
                        onClick={() => handleToggleFree(sq.id, sq.free)}
                        clickable
                      />
                      <IconButton aria-label="delete" onClick={() => handleDelete(sq.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  }
                >
                  {editingSquareId === sq.id ? (
                    <TextField
                      value={editingTitle}
                      onChange={(e) => { setEditingTitle(e.target.value); setEditError(null) }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(sq.id)
                        if (e.key === 'Escape') handleCancelEdit()
                      }}
                      onBlur={() => {
                        if (cancellingRef.current) { cancellingRef.current = false; return }
                        handleSaveEdit(sq.id)
                      }}
                      error={!!editError}
                      helperText={editError}
                      autoFocus
                      fullWidth
                      variant="standard"
                    />
                  ) : (
                    <ListItemText
                      primary={sq.title}
                      onClick={() => handleStartEdit(sq.id, sq.title)}
                      slotProps={{ primary: { style: { cursor: 'text' } } }}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          )}
          </Stack>
        </FormCardBody>
        <FormCardFooter>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </FormCardFooter>
      </FormCard>

      <AddItemDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setAddError(null) }}
        title="Add Square"
        label="Title"
        value={newTitle}
        onChange={(value) => { setNewTitle(value); setAddError(null) }}
        onAdd={handleAdd}
        error={addError}
      />

      <AiGenerateDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        onGenerate={handleGenerate}
        onAddSquares={handleAddSquares}
        defaultTopic={pack.title}
      />
    </Box>
  )
}
